import { get } from 'lodash-es'
import { getQueryParams } from './query'
import { updateDate } from './date'
import {
  WALLET_REDIRECT,
  CARD_PIN_STATUS,
  CARD_PIN_ORIGIN,
  FLOWS,
  isFlowSupported,
} from 'universal'
import { cancelRequests } from 'utils/component'
import { actionTypes } from '../../constants'
import { OPERATIONS, SUB_OPERATIONS, CAPABILITIES } from 'universal/cfs-card'
import { errorCodes } from 'universal/error-codes'

const { OFFLINE_PIN } = CAPABILITIES
const { REISSUE, REPORT_CARD_LOST, FUNDING_OPTIONS, PINS } = OPERATIONS
const {
  REPORT_DIGITAL_CARD_LOST,
  CONVERT_FORM_FACTOR,
  GET_FUNDING_OPTIONS,
  SET_PIN,
  CONVERT_FORM_FACTOR_WITH_FEE,
} = SUB_OPERATIONS

const ESTIMATED_ARRIVAL_DATE_IN_DAYS = 10

function isVirtualCard(card) {
  return get(card, ['productName'], '').includes('VIRTUAL')
}

function hasVirtualCard(cards) {
  return !!Object.values(cards).find((card) => isVirtualCard(card))
}

function getVirtualCard(cards) {
  return Object.values(cards).find((card) => isVirtualCard(card))
}

function getInstrumentThatAttemptedToResolve() {
  const { attemptedInstrument = '' } = getQueryParams() as any
  return attemptedInstrument
}

function getWalletRedirectStatus() {
  const { success = '', attemptedInstrument = '', reason = '' } = getQueryParams() as any
  const { SUCCESS, FAILURE } = WALLET_REDIRECT.STATUS
  const { CANCEL } = WALLET_REDIRECT.REASON

  if (attemptedInstrument) {
    if (success) {
      return SUCCESS
    }
    if (reason !== CANCEL) {
      return FAILURE
    }
  }
  return ''
}

function getContingencyTypeFromFundingSource(fundingSources = []) {
  const attemptedInstrument = getInstrumentThatAttemptedToResolve()
  const fundingSource = fundingSources.find((fs) => fs.id === attemptedInstrument) || {}
  const contingencyDetailsType = get(fundingSource, 'contingencyDetails.type', '')
  return contingencyDetailsType
}

function getCardHolderAddressId(cardInfo: any = {}) {
  return cardInfo?.cardHolder?.addressId
}

/**
 * Cloning the method here from actions/card, becuase Retry & RetryError
 * will have to import this function from actions/card which increases
 * the bundle size
 */
function resetCardRequest(cancelTokens) {
  cancelRequests(cancelTokens)
  return { type: actionTypes.RESET_CARD_REQUEST }
}

function getCardShippingAddressId(card, addresses = [], options: any = {}) {
  if (!card) {
    return null
  }
  const { fallbackToBilling } = options
  const shippingAddressIdFromCardDetails = card.shippingAddressId || card.shipping_address_id
  const matchedShippingAddress = addresses.find(
    (address) => String(address?.id) === String(shippingAddressIdFromCardDetails),
  )
  if (!matchedShippingAddress?.id && fallbackToBilling) {
    const cardHolder = card.cardHolder || card.card_holder || {}
    return cardHolder.addressId || cardHolder.address_id || null
  }
  return shippingAddressIdFromCardDetails || null
}

function parseShipmentEstimatedArrivalDate(dateString: string) {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

function getEstimatedArrivalDate(card) {
  const cardRecord = card || {}
  const shipmentEstimatedArrivalDate =
    cardRecord.shipmentDetails?.shipmentEstimatedArrivalDate ||
    cardRecord.shipment_details?.shipment_estimated_arrival_date

  if (shipmentEstimatedArrivalDate) {
    return parseShipmentEstimatedArrivalDate(String(shipmentEstimatedArrivalDate))
  }

  // Legacy debitcardnodeweb estimatedDeliveryDate.js uses time_created + 10 days when shipment date is missing.
  const timeCreated = cardRecord.timeCreated || cardRecord.time_created
  const baseDate = timeCreated ? new Date(timeCreated) : new Date()
  return updateDate(baseDate, { addDays: ESTIMATED_ARRIVAL_DATE_IN_DAYS })
}

function hasOperationMetadata(cardRes, options) {
  const { operation, subOperation } = options
  if (!cardRes || !operation || !subOperation) {
    return false
  }

  const operationMetadataList = cardRes?.operationMetadata
  if (!operationMetadataList || operationMetadataList.length === 0) {
    return false
  }

  const validOperationMetaData = operationMetadataList.some(
    ({ name: operationName, subOperation: subOperationName }) =>
      operationName === operation && subOperationName === subOperation,
  )

  return validOperationMetaData
}

function hasCapabilities(cardRes, capabilities = []) {
  const cardCapabilities = get(cardRes, 'additionalDetails.capabilities', [])
  if (!cardCapabilities || cardCapabilities.length === 0) {
    return false
  }
  const matchedCapabilities = capabilities.map((capability) =>
    cardCapabilities.includes(capability),
  )
  return matchedCapabilities.length === capabilities.length
}

function shouldSetPin(card, options: any = {}) {
  const { shouldBeSetByUser = true, shouldCheckOfflinePin = false } = options
  return (
    !isPinSet(card.pinDetails, { shouldBeSetByUser }) &&
    (hasOperationMetadata(card, {
      operation: PINS,
      subOperation: SET_PIN,
    }) ||
      (shouldCheckOfflinePin && hasCapabilities(card, [OFFLINE_PIN])))
  )
}

function isRequestPhysicalCardWithFeeAllowed(card, flowName: any = FLOWS.REQUEST_PHYSICAL_CARD) {
  const { productName = '' } = card || {}
  const hasConvertWithFeeOperationMetadata = hasOperationMetadata(card, {
    operation: REISSUE,
    subOperation: CONVERT_FORM_FACTOR_WITH_FEE,
  })
  return isFlowSupported(flowName, productName) && hasConvertWithFeeOperationMetadata
}

function isPinSet(pinDetails, options: any = {}) {
  // shouldBeSetByUser variable checks for origin === USER, otherwise, we ignore origin
  const { shouldBeSetByUser = true } = options
  const { origin = '', status = '' } = pinDetails || {}
  const validOrigin = shouldBeSetByUser ? origin === CARD_PIN_ORIGIN.USER : true
  return status === CARD_PIN_STATUS.SET && validOrigin
}

function isDigitalOnlyCard(cardRes) {
  if (
    hasOperationMetadata(cardRes, {
      operation: REISSUE,
      subOperation: CONVERT_FORM_FACTOR,
    }) ||
    hasOperationMetadata(cardRes, {
      operation: REISSUE,
      subOperation: CONVERT_FORM_FACTOR_WITH_FEE,
    }) ||
    hasOperationMetadata(cardRes, {
      operation: REPORT_CARD_LOST,
      subOperation: REPORT_DIGITAL_CARD_LOST,
    })
  ) {
    return true
  }
  return false
}

function hasFundingOptionsOperation(card) {
  return hasOperationMetadata(card, {
    operation: FUNDING_OPTIONS,
    subOperation: GET_FUNDING_OPTIONS,
  })
}

function isFundingFlow(path = '') {
  const regex = /^\/myaccount\/debitcards\/manage\/[a-zA-Z0-9-]+\/funding(\?.*)?/ //NOSONAR
  return regex.test(path)
}

function isUnsupportedOperation(err: any = {}) {
  return err.includes(errorCodes.UNSUPPORTED_OPERATION)
}

export {
  isVirtualCard,
  hasVirtualCard,
  getVirtualCard,
  getInstrumentThatAttemptedToResolve,
  getWalletRedirectStatus,
  getContingencyTypeFromFundingSource,
  getCardHolderAddressId,
  resetCardRequest,
  getCardShippingAddressId,
  getEstimatedArrivalDate,
  hasOperationMetadata,
  hasCapabilities,
  isPinSet,
  shouldSetPin,
  isDigitalOnlyCard,
  hasFundingOptionsOperation,
  isFundingFlow,
  isUnsupportedOperation,
  isRequestPhysicalCardWithFeeAllowed,
}
